import React from 'react';

import { ConfigMenu } from '@reckon-web/config-menu';
import { IconButton } from '@reckon-web/button';
import { Flex } from '@reckon-web/flex';
import { Text } from '@reckon-web/text';
import { TextLinkButton } from '@reckon-web/text-link';
import { Tooltip } from '@reckon-web/tooltip';
import { makeId } from '@reckon-web/utils';
import { Edit3Icon, FilterIcon, Trash2Icon } from '@reckon-web/icon';

import { useDeleteUserFilter } from '../../hooks/use-delete-user-filter';
import { useEditUserFilter } from '../../hooks/use-edit-user-filter';
import { GetUserFilterListResult } from '../../data/useGetUserFilterList';

import { WorkViewEnum } from './types/work-view-enum';
import { KanbanOrList } from './kanban-or-list';

export const VisibleFilters: React.FC<{
  userFilters: GetUserFilterListResult[];
  applyUserFilter?: (userFilter: GetUserFilterListResult) => void;
  setKanbanOrList: (kanbanOrList: KanbanOrList) => void;
  testId?: string;
}> = ({
  userFilters,
  applyUserFilter,
  setKanbanOrList,
  testId = `visible-filters`,
}) => {
  const [EditUserFilterDialog, editUserFilter] = useEditUserFilter();
  const [DeleteUserFilterDialog, deleteUserFilter] = useDeleteUserFilter();

  React.useEffect(() => {
    const userFilter = userFilters.find((filter) => filter.isDefault);

    if (!!userFilter && !!applyUserFilter) {
      applyUserFilter(userFilter);

      // Hack, this should probably exist in KanbanOrListToggle component.
      // But since the saved filters will be removed in the near future, this is fine.
      if (
        userFilter.workView === WorkViewEnum.valuesEnum.JobBoard ||
        userFilter.workView === WorkViewEnum.valuesEnum.TaskBoard
      ) {
        setKanbanOrList('kanban');
      }

      if (
        userFilter.workView === WorkViewEnum.valuesEnum.JobList ||
        userFilter.workView === WorkViewEnum.valuesEnum.TaskList
      ) {
        setKanbanOrList('list');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      <ConfigMenu
        data-testid={testId}
        trigger={({ triggerProps }) => (
          <Tooltip content="Saved filters and more filters">
            <IconButton
              label="Saved filters and more filters"
              icon={FilterIcon}
              size="small"
              weight="none"
              tone="passive"
              data-testid={makeId(testId, 'saved-filters')}
              {...triggerProps}
            />
          </Tooltip>
        )}
      >
        <Flex
          direction="column"
          gap="medium"
          paddingX="medium"
          paddingY="small"
          data={{ testid: makeId(testId, 'user-filters') }}
        >
          <Text size="xsmall" color="muted">
            Saved filters
          </Text>
          {(!!userFilters.length &&
            !!applyUserFilter &&
            userFilters.map((userFilter) => {
              const localTestId = makeId(
                testId,
                'user-filter',
                userFilter.cloudId
              );
              return (
                <Flex key={userFilter.cloudId} alignItems="center" gap="medium">
                  <Flex direction="column" gap="xxsmall" flexGrow={1}>
                    <Text size="small" weight="medium" align="left">
                      <TextLinkButton
                        color="base"
                        onClick={() => applyUserFilter(userFilter)}
                        data-testid={makeId(localTestId, 'text-link')}
                      >
                        {`${userFilter.name} ${
                          (userFilter.isDefault && '(default)') || ''
                        }`}
                      </TextLinkButton>
                    </Text>
                  </Flex>
                  <Flex>
                    <Tooltip content="Edit">
                      <IconButton
                        icon={Edit3Icon}
                        label="Edit"
                        size="small"
                        tone="active"
                        weight="none"
                        onClick={() => editUserFilter(userFilter)}
                        data-testid={`${testId}__user-filter__edit-${userFilter.cloudId}`}
                      />
                    </Tooltip>
                    <Tooltip content="Delete">
                      <IconButton
                        icon={Trash2Icon}
                        label="Delete"
                        size="small"
                        tone="passive"
                        weight="none"
                        onClick={() => deleteUserFilter(userFilter)}
                        data-testid={`${testId}__user-filter__delete-${userFilter.cloudId}`}
                      />
                    </Tooltip>
                  </Flex>
                </Flex>
              );
            })) || (
            <Text size="small" weight="medium" color="dim">
              No filters saved yet
            </Text>
          )}
        </Flex>
      </ConfigMenu>
      <EditUserFilterDialog />
      <DeleteUserFilterDialog />
    </React.Fragment>
  );
};
